import React, { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Icons,
  InputBase,
  useTheme,
} from '@ublend-npm/aulaui-core';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { createStyles } from './SubmissionSearch.styles';
import {
  createSubmissionSearchResults,
  filterSubmissionSearchResults,
  getSubmissionStudentName,
  SubmissionSearchGroups,
  SubmissionSearchItem,
  SubmissionSearchResult,
} from './submissionSearch.utils';

type Props<T extends SubmissionSearchItem> = {
  children: ReactNode;
  filterControl?: ReactNode;
  getStudentName?: (submission: T) => string;
  isVisible: boolean;
  renderResults: (results: SubmissionSearchResult<T>[]) => ReactNode;
  submissionGroups: SubmissionSearchGroups<T>;
};

const SubmissionSearch = <T extends SubmissionSearchItem>({
  children,
  filterControl,
  getStudentName = getSubmissionStudentName,
  isVisible,
  renderResults,
  submissionGroups,
}: Props<T>) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [query, setQuery] = useState('');
  const isSearchActive = query.trim().length > 0;

  const allResults = useMemo(
    () => createSubmissionSearchResults(submissionGroups),
    [submissionGroups]
  );
  const matchingResults = useMemo(
    () => filterSubmissionSearchResults(allResults, query, getStudentName),
    [allResults, getStudentName, query]
  );

  if (!isVisible) return <>{children}</>;

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.controls}>
        {filterControl && <Box sx={styles.filterControl}>{filterControl}</Box>}
        <Box sx={styles.searchWrapper}>
          <InputBase
            fullWidth
            inputProps={{
              'aria-label': 'Search for students',
              role: 'searchbox',
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(event.target.value)
            }
            placeholder="Search for students"
            startAdornment={
              <Icons.Search aria-hidden sx={{ color: 'text.secondary' }} />
            }
            sx={styles.searchInput}
            value={query}
          />
          {query && (
            <IconButton
              aria-label="Clear student search"
              onClick={() => setQuery('')}
              size="small"
              sx={styles.clearButton}
              type="button"
            >
              <Icons.Close aria-hidden fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {!isSearchActive ? (
        children
      ) : matchingResults.length ? (
        renderResults(matchingResults)
      ) : (
        <NoResultsCard title="No results found" sx={styles.noResults} />
      )}
    </Box>
  );
};

export default SubmissionSearch;
